import { useTranslation } from '@src/hooks/useTranslation'
import { useWindowDimensions } from '@src/hooks/useWindowDimensions'
import classNames from 'classnames'
import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { NavButtons } from '../nav-buttons/NavButtons.component'
import styles from './Carousel.module.scss'

interface CarouselProps {
  children?: ReactNode
  className?: string
  enableOnAllSizes?: boolean
  isActive?: boolean
  ab349?: boolean
}

export function Carousel({
  className,
  children,
  enableOnAllSizes,
  isActive,
  ab349,
}: CarouselProps): ReactNode {
  const translate = useTranslation()
  const { width } = useWindowDimensions()
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    skipSnaps: false,
    containScroll: 'trimSnaps',
    // @ts-expect-error
    enabled: enableOnAllSizes || isActive,
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const [allItemsFit, setAllItemsFit] = useState(false)
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true) // Start with disabled left button
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false)
  const [_, setSelectedIndex] = useState(0)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((emblaApi: UseEmblaCarouselType[1]) => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)

    // Initial check for button states
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  useEffect(() => {
    // Check if all items fit in the container and center them if no need to scroll
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth
      const totalWidth = containerRef.current.scrollWidth

      setAllItemsFit(totalWidth <= containerWidth)
    }
  }, [children, width])

  return (
    <div
      className={classNames(
        styles.Carousel,
        className && styles[className],
        ab349 && styles.Carousel__ab349,
      )}
    >
      <div className={styles.Carousel__viewport} ref={emblaRef}>
        <div
          ref={containerRef}
          className={classNames(styles.Carousel__container, {
            [styles.Carousel__container___centered]: allItemsFit,
          })}
        >
          {children}
        </div>
      </div>
      {isActive && (
        <NavButtons
          onPrevClick={scrollPrev}
          onNextClick={scrollNext}
          prevDisabled={prevBtnDisabled}
          nextDisabled={nextBtnDisabled}
          prevAriaLabel={translate('aria-scroll-left')}
          nextAriaLabel={translate('aria-scroll-right')}
        />
      )}
    </div>
  )
}
